import { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { getPageMetadata } from "../../server/render/siteChrome/getPageMetadata";
import { getPageName } from "../../state/modules/page/selectors";
import { getCategoryId } from "../../state/modules/category/selectors";
import { CATEGORY_PAGE } from "../../state/modules/page/constants";

const refreshPageAnalytics = (siteChrome) => {
  // Trigger the Site Chrome API to refresh Mixpanel super properties and fire pageData event
  if (typeof siteChrome.refreshPageAnalytics === "function") {
    siteChrome.refreshPageAnalytics();
  }
};

const applyPageAnalytics = (
  siteChrome,
  { analyticsPageId, analyticsPageType },
) => {
  const currentProperties = siteChrome.mixpanelSuperProperties || {};

  // Site Chrome already injects these from the SSR-rendered page metadata and
  // fires the initial pageData itself. If the properties already match, skip to
  // avoid emitting a duplicate pageData event for the current page. Only fire on
  // an actual change (client-side navigation).
  if (
    currentProperties.analyticsPageId === analyticsPageId &&
    currentProperties.analyticsPageType === analyticsPageType
  ) {
    return;
  }

  // Use property names expected by Site Chrome (it will map them to page_id/page_type for Mixpanel)
  siteChrome.mixpanelSuperProperties = { analyticsPageId, analyticsPageType };

  refreshPageAnalytics(siteChrome);
};

const usePageAnalyticsUpdates = () => {
  const { analyticsPageId, analyticsPageType } = useSelector(
    getPageMetadata,
    shallowEqual,
  );
  const shouldSkipCategoryPageAnalytics = useSelector(
    (state) =>
      getPageName(state) === CATEGORY_PAGE && getCategoryId(state) == null,
  );

  useEffect(() => {
    const siteChrome = window.asos?.siteChrome;

    if (!siteChrome) {
      return;
    }

    // For category pages with undefined categoryId, only clear if we have stale analytics data
    // (Skip on initial load when neither categoryId nor analytics are ready)
    if (shouldSkipCategoryPageAnalytics) {
      if (analyticsPageId || analyticsPageType) {
        // We have stale analytics data but no categoryId - clear it during errors/transitions
        siteChrome.mixpanelSuperProperties = {};
        refreshPageAnalytics(siteChrome);
      }
      // Otherwise skip entirely - data not ready yet on initial load

      return;
    }

    if (analyticsPageId && analyticsPageType) {
      applyPageAnalytics(siteChrome, { analyticsPageId, analyticsPageType });
    }
  }, [analyticsPageId, analyticsPageType, shouldSkipCategoryPageAnalytics]);
};

export default usePageAnalyticsUpdates;
